import type { ModelRelease, Organization } from "../types/model";
import { curatedModelSearchTerms } from "../data/curated-models";
import { detailValueSearchTerms } from "./detail-value-localization";
import { isIndexedDetailPath } from "./search-index-paths";

export type Language = "en" | "zh";

const EN_COPY = {
  searchPlaceholder: "Search name, vendor, capability, tag…",
  searchAria: "Search model releases",
  group: "Group",
  sort: "Sort",
  filters: "Filters",
  filtersActive: "active",
  groupDate: "Date",
  groupCompany: "Company",
  groupCategory: "Category",
  groupNone: "None",
  sortNewest: "Newest first",
  sortOldest: "Oldest first",
  sortCompanyName: "Company name",
  sortCategoryName: "Category name",
  sortLatestRelease: "Latest release",
  sortModelCount: "Model count",
  sortModelName: "Model name",
  sortCompany: "Company",
  sortCategory: "Category",
  sortReleaseStatus: "Release status",
  switchToChinese: "Switch to Chinese",
  switchToEnglish: "Switch to English",
  activeFilters: "Active filters",
  active: "Active",
  search: "Search",
  date: "Date",
  any: "Any",
  clearAll: "Clear all",
  modelDataLoadError: "Model data could not be loaded",
  loadingModelReleases: "Loading model releases",
  loadingSnapshot: "Reading the current verified snapshot…",
  loadingModelDetails: "Loading model details",
  loadingModelDetailsHint: "Preparing the detail view…",
  modelDetailsLoadError: "Model details could not be loaded",
  modelDetailsLoadErrorHint:
    "The application may have been updated. Reload the page and try again.",
  reloadPage: "Reload page",
  closeDetails: "Close model details",
  noModelsMatch: "No models match",
  noModelsMatchHint: "Try removing a filter or widening the date range.",
  clearEverything: "Clear everything",
  showing: "showing",
  of: "of",
  matches: "matches",
  models: "models",
  releaseGroup: "Release group",
  openGroup: "Open release group",
  closeGroup: "Close release group",
  backToGroup: "Back to",
  groupModels: "Models",
  groupMatchHint: "match the current search and filters",
  firstRelease: "First release",
  latestRelease: "Latest release",
  groupedBy: "grouped by",
  loadMore: "more",
  curatedRecords: "curated records",
  data: "data",
  keyboardSearch: "search",
  keyboardMove: "move",
  keyboardOpen: "open",
  keyboardClose: "close",
  backToTop: "Back to top",
  closeFilters: "Close filters",
  filterDescription: "Refine the release log without another network request.",
  filterBy: "Filter by",
  removeFilter: "Remove filter",
  company: "Company",
  category: "Category",
  capability: "Capability",
  releaseStatus: "Release status",
  openness: "Openness",
  find: "Find",
  showingFacet: "Showing",
  searchToNarrow: "search to narrow.",
  releaseDate: "Release date",
  from: "From",
  to: "To",
  reset: "Reset",
  done: "Done",
  previousMonth: "Previous month",
  nextMonth: "Next month",
  openCalendar: "Open date calendar",
  year: "year",
  month: "month",
  day: "day",
  new: "New",
  capabilities: "Capabilities",
  openDetails: "Open details for",
  by: "by",
  today: "Today",
  flagship: "Flagship",
  milestone: "Milestone",
  expandFlagship: "Show flagship models",
  collapseFlagship: "Hide flagship models",
  expandMilestone: "Show milestone models",
  collapseMilestone: "Hide milestone models",
  upcoming: "Upcoming",
  expectedRelease: "Expected release",
  expandUpcoming: "Show upcoming models",
  collapseUpcoming: "Hide upcoming models",
  yesterday: "Yesterday",
  dateUnknown: "No Data",
  verificationPending: "Verification pending",
  monthPrecision: "Month precision",
  yearPrecision: "Year precision",
  latest: "Latest",
  model: "model",
  record: "record",
  release: "release",
  previousModel: "Previous model",
  nextModel: "Next model",
  firstAnnounced: "First announced",
  firstAvailable: "First available",
  lastUpdated: "Last updated",
  inputOutput: "Input & output",
  input: "Input",
  output: "Output",
  live: "Live",
  supported: "supported",
  notRecordedLower: "no data",
  releaseAvailability: "Release & availability",
  announcement: "Announcement",
  webApp: "Web / app",
  weights: "Weights",
  officialAnnouncement: "Official announcement",
  public: "Public",
  notRecorded: "No Data",
  unrecordedFields: "unrecorded fields",
  hideUnrecordedFields: "Hide unrecorded fields",
  resources: "Resources",
  openModel: "Open model",
  license: "License",
  notPublished: "No Data",
  weightFormats: "Weight formats",
  commercialUse: "Commercial use",
  source: "Source",
  capabilitiesPositioning: "Capabilities & positioning",
  noCapabilities: "No Data.",
  technical: "Technical",
  architecture: "Architecture",
  parameters: "Parameters",
  experts: "Experts",
  contextLength: "Context length",
  nativeContextLength: "Model-native context",
  openRouterContextLength: "OpenRouter context",
  activation: "Activation",
  attention: "Attention",
  reasoning: "Reasoning",
  architectureDetails: "Architecture details",
  officialIdAlias: "Official ID / alias",
  identityModel: "Model",
  identityProduct: "Product",
  switchIdentityTo: "Switch to",
  fastMode: "Acceleration artifact",
  modelRelations: "Model relations",
  modelFamily: "Model family",
  ownerGroup: "Owner group",
  modelRole: "Model role",
  aliases: "Aliases",
  actualDeveloper: "Actual developer",
  baseModel: "Base model",
  baseModelOfficialId: "Base model official ID / alias",
  baseModelRecordId: "Base model catalog ID",
  compatibleBaseModels: "Compatible base models",
  internalComponents: "Internal components",
  successor: "Successor",
  variants: "Variants",
  checkpoints: "Checkpoints & artifacts",
  siblingModels: "Sibling models",
  apiAccess: "API access",
  apiMetadata: "API metadata",
  audioInput: "Audio input",
  audioOutput: "Audio output",
  platformUsage: "Platform access",
  platformUsageDescription:
    "Shared platform access and non-pricing service terms.",
  accessChannel: "Access channel",
  publicApiStatus: "Public API status",
  currentModelTerms: "Current model terms",
  publishedRateExamples: "Published platform terms",
  platformSource: "Platform source",
  generation: "Generation",
  generationDescription:
    "Model-specific inference, checkpoint, resolution, and real-time generation facts.",
  additionalSpecifications: "Additional specifications",
  additionalSpecificationsDescription:
    "Verified model-specific requirements, output, storage, availability, and research facts.",
  availabilityDetails: "Availability details",
  contextDetails: "Context details",
  embodiment: "Embodiment",
  performance: "Performance",
  interaction: "Interaction",
  languageSupport: "Language support",
  requirements: "Requirements",
  outputSpecifications: "Output specifications",
  audioSpecifications: "Audio specifications",
  storage: "Storage",
  researchArtifacts: "Research artifacts",
  knowledgeCutoff: "Knowledge cutoff",
  modelCard: "Model card",
  dateEvidence: "Date evidence",
  languages: "Languages",
  inputFormats: "Input formats",
  training: "Training",
  apiVariants: "Model variants",
  versionHistory: "Version history",
  versionHistoryDescription:
    "Dated snapshots and behavior revisions retained under one marketed model identity.",
  marketedIdentity: "Aggregation scope",
  versionAliases: "Version IDs & aliases",
  snapshot: "Snapshot",
  historicalIdentity: "Historical identity",
  historicalIdentityDescription:
    "Earlier names and specifications retained under the current model record.",
  formerName: "Former name",
  introduced: "Introduced",
  historicalAliases: "Historical aliases",
  releaseTimeline: "Release timeline",
  noTimeline: "No Data.",
  linksSources: "Links & sources",
  noPublicSource: "No Data.",
  releaseFactsVerification: "Release facts & verification",
  datePrecision: "Date precision",
  recordType: "Record type",
  scope: "Scope",
  confidence: "Confidence",
  verification: "Verification",
  sourceTier: "Evidence tier",
  sourceCount: "Source entries",
  opensInNewTab: "opens in a new tab",
  lastChecked: "Last checked",
  primarySource: "Primary source",
  fastModeSource: "Acceleration artifact source",
  total: "total",
  activeParameters: "active"
} as const;

export type CopyKey = keyof typeof EN_COPY;

/**
 * The Simplified Chinese tables are the largest block of copy in the
 * application and the interface always starts in English, so they live in
 * their own module and are fetched only when the language button is used.
 * Every reader below stays synchronous and falls back to the English source
 * value while the tables are absent, which is also the honest result when the
 * request fails.
 */
export interface ChineseCopy {
  copy: Record<CopyKey, string>;
  category: Record<string, string>;
  status: Record<string, string>;
  openness: Record<string, string>;
  modality: Record<string, string>;
  capability: Record<string, string>;
  structured: Record<string, string>;
  role: Record<string, string>;
  summary: Record<string, string>;
  translateTechnicalPhrase: (value: string) => string;
}

let chineseCopy: ChineseCopy | null = null;
let chineseCopyRequest: Promise<ChineseCopy> | null = null;

export function loadChineseCopy(): Promise<ChineseCopy> {
  chineseCopyRequest ??= import("./localization-zh")
    .then((module) => {
      chineseCopy = module.CHINESE_COPY;
      return chineseCopy;
    })
    .catch((reason: unknown) => {
      chineseCopyRequest = null;
      throw reason;
    });
  return chineseCopyRequest;
}

export function chineseCopyLoaded(): boolean {
  return chineseCopy !== null;
}

export function ui(language: Language, key: CopyKey): string {
  if (language !== "zh") return EN_COPY[key];
  return chineseCopy?.copy[key] ?? EN_COPY[key];
}

export type DataValueKind =
  | "category"
  | "capability"
  | "modality"
  | "status"
  | "openness"
  | "role"
  | "scope"
  | "recordType"
  | "confidence"
  | "precision"
  | "fastMode"
  | "sourceType"
  | "sourceKind"
  | "sourceTier"
  | "verification"
  | "channel"
  | "license"
  | "technical"
  | "summary"
  | "linkLabel"
  | "linkGroup";

/**
 * Source kinds and tiers are small controlled vocabularies rendered in the
 * detail header, which paints before the deferred Chinese tables load. Keeping
 * both languages here avoids an English flash on the one element that is
 * always above the fold.
 */
const SOURCE_KIND_LABELS: Record<string, readonly [string, string]> = {
  "official-page": ["Official site", "官网"],
  "official-docs": ["Official docs", "官方文档"],
  "model-card": ["Model card", "模型卡"],
  huggingface: ["Hugging Face", "Hugging Face"],
  github: ["GitHub", "GitHub"],
  "model-repository": ["Model repository", "模型仓库"],
  paper: ["Paper", "论文"],
  press: ["Press", "新闻稿"],
  aggregator: ["Aggregator", "聚合平台"],
  social: ["Social", "社交媒体"],
  reference: ["Reference", "参考资料"],
  other: ["Link", "链接"]
};

const SOURCE_TIER_LABELS: Record<string, readonly [string, string]> = {
  official: ["Official", "官方"],
  "first-party-partner": ["Launch partner", "发布伙伴"],
  "third-party": ["Third-party", "第三方"],
  secondary: ["Secondary", "二手报道"]
};

export function localizeDataValue(
  value: string | null | undefined,
  kind: DataValueKind,
  language: Language
): string {
  if (!value) return "";
  if (kind === "sourceKind" || kind === "sourceTier") {
    const labels =
      kind === "sourceKind"
        ? SOURCE_KIND_LABELS[value]
        : SOURCE_TIER_LABELS[value];
    return labels ? labels[language === "zh" ? 1 : 0] : value;
  }
  if (/^(unknown|not recorded|not published)$/i.test(value)) {
    return language === "zh" ? "无数据" : "No Data";
  }
  if (language === "en") return value;

  const chinese = chineseCopy;
  if (!chinese) return value;

  const exact =
    kind === "category"
      ? chinese.category[value]
      : kind === "capability"
        ? chinese.capability[value]
        : kind === "modality"
          ? chinese.modality[value]
          : kind === "status"
            ? chinese.status[value]
            : kind === "openness"
              ? chinese.openness[value]
              : kind === "role"
                ? chinese.role[value]
                : kind === "summary"
                  ? chinese.summary[value]
                  : chinese.structured[value];

  return exact ?? chinese.translateTechnicalPhrase(value);
}

export function localizeFacetValue(
  facet: "companies" | "categories" | "capabilities" | "statuses" | "openness",
  value: string,
  language: Language,
  organizations?: ReadonlyMap<string, Organization>
): string {
  if (facet === "companies") return organizations?.get(value)?.names[language] ?? value;
  const kind: DataValueKind =
    facet === "categories"
      ? "category"
      : facet === "capabilities"
        ? "capability"
        : facet === "statuses"
          ? "status"
          : "openness";
  return localizeDataValue(value, kind, language);
}

function nestedDetailStrings(
  value: unknown,
  path = ""
): Array<{ path: string; value: string }> {
  if (typeof value === "string") return [{ path, value }];
  if (Array.isArray(value)) {
    return value.flatMap((item) => nestedDetailStrings(item, `${path}[]`));
  }
  if (value && typeof value === "object") {
    return Object.entries(value).flatMap(([key, item]) =>
      nestedDetailStrings(item, path ? `${path}.${key}` : key)
    );
  }
  return [];
}

export function localizedRecordSearchText(
  record: ModelRelease,
  language: Language
): string {
  const curatedTerms = curatedModelSearchTerms(record.id);
  const localizedDetailTerms = nestedDetailStrings(record.details)
    .filter(({ path }) => isIndexedDetailPath(path))
    .flatMap(({ path, value }) => detailValueSearchTerms(path, value, language));
  if (language === "en") {
    return [record.searchText, curatedTerms, ...localizedDetailTerms]
      .filter(Boolean)
      .join(" ")
      .toLocaleLowerCase("en-US");
  }
  return [
    record.searchText,
    curatedTerms,
    localizeDataValue(record.mainCategory, "category", language),
    ...record.capabilities.map((value) =>
      localizeDataValue(value, "capability", language)
    ),
    ...record.modalities.input.map((value) =>
      localizeDataValue(value, "modality", language)
    ),
    ...record.modalities.output.map((value) =>
      localizeDataValue(value, "modality", language)
    ),
    localizeDataValue(record.release.status, "status", language),
    localizeDataValue(record.release.openness, "openness", language),
    localizeDataValue(record.modelRole, "role", language),
    localizeDataValue(record.source.type, "sourceType", language),
    localizeDataValue(record.verificationStatus, "verification", language),
    ...(record.notes
      ? detailValueSearchTerms("notes", record.notes, language)
      : []),
    ...localizedDetailTerms
  ]
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase("zh-CN");
}

export function localeFor(language: Language): string {
  return language === "zh" ? "zh-CN" : "en-US";
}
